import React from 'react';
import { Button } from '@deque/cauldron-react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import Features from './Features';
import styles from './ProductCard.css';

type ActionElementProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  React.AnchorHTMLAttributes<HTMLAnchorElement>;

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

const disableClick = (e: React.MouseEvent) => {
  e.preventDefault();
};

interface ActionProps {
  action: string | (() => void);
  disabled: boolean;
  type: string;
  onClick: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  children: React.ReactNode;
}

const Action: React.FC<ActionProps> = ({
  action,
  disabled,
  type,
  onClick,
  children
}) => {
  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
      onClick(e);
      if (typeof action === 'function') {
        action();
      }
    },
    [onClick, action]
  );
  const isLink = typeof action === 'string';
  const isAbsolute = isLink && action.startsWith('http');
  const actionProps: ActionElementProps = {
    'aria-disabled': !!disabled,
    onClick: disabled ? disableClick : handleClick,
    className: classNames(isLink && `Button--${type}`, styles.contact),
    children
  };

  if (isLink) {
    return isAbsolute ? (
      <a href={action} data-testid="action" {...actionProps} />
    ) : (
      <Link to={action} data-testid="action" {...actionProps} />
    );
  }

  return (
    <Button
      variant={type === 'secondary' ? 'secondary' : 'primary'}
      data-testid="action"
      {...actionProps}
    />
  );
};

interface ShortCardProps {
  smallPrintText: string | React.ReactElement;
  actionText: string;
  action: (() => void) | string;
  buttonType: string;
  onClick?: (
    e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
  ) => void;
}

const ShortCard = ({
  smallPrintText,
  actionText,
  action,
  buttonType,
  onClick = noop
}: ShortCardProps): React.ReactElement => {
  return (
    <>
      <div className={styles.smallPrint}>
        <p>{smallPrintText}</p>
      </div>
      <Action
        action={action}
        disabled={false}
        onClick={onClick}
        type={buttonType}
      >
        {actionText}
      </Action>
    </>
  );
};

interface LongCardProps extends ShortCardProps {
  buttonDisable: boolean;
  pricePreamble?: string;
  pricePostamble?: React.ReactElement;
  subAction?: string;
  subActionText?: string;
}

const LongCard = ({
  smallPrintText,
  actionText,
  buttonType,
  buttonDisable,
  action,
  pricePreamble,
  pricePostamble,
  onClick = noop,
  subAction,
  subActionText
}: LongCardProps): React.ReactElement => {
  return (
    <>
      <div className={styles.pricePre} data-testid="preamble">
        {pricePreamble || '\xa0'}
      </div>
      <Action
        action={action}
        disabled={buttonDisable}
        onClick={onClick}
        type={buttonType}
      >
        {actionText}
      </Action>
      <div className={styles.pricePost} data-testid="postamble">
        {pricePostamble || '\xa0'}
      </div>
      {subAction && subActionText && (
        <div className={styles.pricePost} data-testid="subaction">
          <Link to={subAction}>{subActionText}</Link>
        </div>
      )}
      <div className={styles.smallPrint}>
        <p>{smallPrintText}</p>
      </div>
    </>
  );
};

export interface Props extends LongCardProps {
  productName: string;
  productMessage: string;
  statusMessage?: string;
  compareProduct?: string;
  featureList: string[];
  decorationStyle?: 'redblue' | 'greenyellow';
  variant: 'short' | 'long';
  statusVariant?: 'success' | 'warn' | 'alert';
}

const ProductCard = ({
  productName,
  productMessage,
  statusMessage,
  compareProduct,
  featureList,
  buttonType,
  action,
  actionText,
  smallPrintText,
  pricePreamble,
  pricePostamble,
  decorationStyle,
  variant,
  buttonDisable,
  statusVariant,
  onClick,
  subAction,
  subActionText
}: Props): React.ReactElement => {
  const bubbles =
    decorationStyle === 'redblue'
      ? styles.redBlueBubbles
      : decorationStyle === 'greenyellow'
      ? styles.greenYellowBubbles
      : null;
  return (
    <div
      className={classNames(
        styles.product,
        bubbles,
        variant === 'short' && styles.short
      )}
      data-testid={`productcard-${productName}`}
    >
      <h2>{productName}</h2>
      <p>{productMessage}</p>
      {statusMessage && (
        <div className={classNames(styles.badgeContainer)}>
          <div
            className={classNames(styles.statusMessage, {
              [styles.warn]: statusVariant === 'warn',
              [styles.alert]: statusVariant === 'alert'
            })}
            data-testid="statusmessage"
          >
            {statusMessage}
          </div>
        </div>
      )}
      <Features
        className={styles.features}
        compareProduct={compareProduct}
        featureList={featureList}
      />
      {variant === 'short' ? (
        <ShortCard
          smallPrintText={smallPrintText}
          actionText={actionText}
          buttonType={buttonType}
          action={action}
          onClick={onClick}
        />
      ) : (
        <LongCard
          smallPrintText={smallPrintText}
          actionText={actionText}
          buttonType={buttonType}
          buttonDisable={buttonDisable}
          action={action}
          pricePreamble={pricePreamble}
          pricePostamble={pricePostamble}
          onClick={onClick}
          subAction={subAction}
          subActionText={subActionText}
        />
      )}
    </div>
  );
};

export default ProductCard;
